import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, FileText, Upload, CheckCircle2, AlertCircle, Loader2, Trash2 } from 'lucide-react';
import { supabase } from '../../../../src/lib/supabase';

interface DocumentUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    negotiationId: string;
    onSuccess: () => void;
}

interface FileEntry {
    id: string;
    file: File | null;
    customName: string;
}

const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
    isOpen,
    onClose,
    negotiationId,
    onSuccess
}) => {
    const [fileEntries, setFileEntries] = useState<FileEntry[]>([{ id: '1', file: null, customName: '' }]);
    const [loading, setLoading] = useState(false);
    const prevIsOpenRef = useRef(false);
    const backdropMouseDownRef = useRef(false);

    // Reset state when modal opens
    useEffect(() => {
        const justOpened = isOpen && !prevIsOpenRef.current;
        prevIsOpenRef.current = isOpen;
        if (!isOpen || !justOpened) return;

        if (isOpen) {
            setFileEntries([{ id: Date.now().toString(), file: null, customName: '' }]);
            setLoading(false);
        }
    }, [isOpen]);



    const handleAddEntry = () => {
        setFileEntries(prev => [
            ...prev,
            { id: Date.now().toString(), file: null, customName: '' }
        ]);
    };

    const handleRemoveEntry = (id: string) => {
        if (fileEntries.length === 1) {
            // If it's the last one, just clear it instead of removing
            setFileEntries([{ id: Date.now().toString(), file: null, customName: '' }]);
            return;
        }
        setFileEntries(prev => prev.filter(entry => entry.id !== id));
    };

    const handleFileChange = (id: string, file: File | null) => {
        setFileEntries(prev => prev.map(entry =>
            entry.id === id ? { ...entry, file: file, customName: file ? file.name.split('.')[0] : '' } : entry
        ));
    };

    const handleNameChange = (id: string, name: string) => {
        setFileEntries(prev => prev.map(entry =>
            entry.id === id ? { ...entry, customName: name } : entry
        ));
    };

    const handleSubmit = async () => {
        // Validate
        const validEntries = fileEntries.filter(e => e.file !== null && e.customName.trim() !== '');

        if (validEntries.length === 0) {
            alert('Por favor, adicione pelo menos um arquivo e defina seu nome.');
            return;
        }

        setLoading(true);
        try {
            // 1. Fetch Webhook URL & Negotiation Data
            const [{ data: settings }, { data: negotiationData }] = await Promise.all([
                supabase.from('system_settings').select('value').eq('key', 'documents_upload_webhook_url').single(),
                supabase.from('negociacoes').select('titulo, pasta_sharepoint_url, documentos_json').eq('id', negotiationId).single()
            ]);

            if (!settings?.value) {
                alert('URL de envio de documentos não configurada. Por favor, configure em Configurações > APIs.');
                setLoading(false);
                return;
            }

            // 2. Prepare FormData
            const formData = new FormData();

            // Add Metadata
            formData.append('id', negotiationId);
            formData.append('titulo', negotiationData?.titulo || '');
            formData.append('pasta_sharepoint_url', negotiationData?.pasta_sharepoint_url || '');
            formData.append('documentos_json', JSON.stringify(negotiationData?.documentos_json || []));

            // Add Files and Names
            validEntries.forEach((entry, index) => {
                if (entry.file) {
                    formData.append(`files`, entry.file);
                    formData.append(`file_names`, entry.customName); // Sending as array of names corresponding to files
                }
            });

            // 3. Send Request
            // #6304: o aviso antigo ("Verifique a configuração do webhook") saía para
            // qualquer falha — inclusive com o n8n fora do ar, que é o caso comum.
            // Separa "não respondeu" de "respondeu com erro"; os arquivos escolhidos
            // continuam no modal para tentar de novo sem escolher tudo outra vez.
            let response: Response;
            try {
                response = await fetch(settings.value, {
                    method: 'POST',
                    body: formData // Fetch handles Content-Type for FormData (multipart/form-data) automatically
                });
            } catch (erroRede) {
                console.error('Serviço de documentos não respondeu:', erroRede);
                alert('O serviço de envio de documentos está fora do ar agora. Tente de novo em alguns minutos — os arquivos escolhidos continuam aqui.');
                return;
            }

            if (response.ok) {
                alert('Documentos enviados com sucesso!');
                onSuccess();
                onClose();
            } else {
                console.error('Serviço de documentos respondeu com erro:', response.status);
                alert(`O envio dos documentos falhou (erro ${response.status}). Tente de novo; se continuar, avise o TI.`);
            }

        } catch (error) {
            console.error('Error sending documents:', error);
            alert('Erro ao preparar o envio dos documentos. Tente de novo; se continuar, avise o TI.');
        } finally {
            setLoading(false);
        }
    };

    return createPortal(
        <div
            className={`fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in ${!isOpen ? 'hidden' : ''}`}
            onMouseDown={e => { backdropMouseDownRef.current = e.target === e.currentTarget; }}
            onClick={() => { if (backdropMouseDownRef.current) onClose(); backdropMouseDownRef.current = false; }}
        >
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-slate-700/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-500 rounded-lg">
                            <Upload size={20} />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100">Adicione os arquivos para upload</h2>
                            <p className="text-sm text-gray-500 dark:text-slate-400">Selecione e nomeie seus documentos</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body - Scrollable */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    {fileEntries.map((entry, index) => (
                        <div key={entry.id} className="p-4 border border-gray-200 dark:border-slate-700 rounded-xl bg-gray-50 dark:bg-slate-800/50 group relative">
                            {/* Remove Button (if more than 1) */}
                            {fileEntries.length > 1 && (
                                <button
                                    onClick={() => handleRemoveEntry(entry.id)}
                                    className="absolute top-2 right-2 p-1.5 text-gray-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                >
                                    <Trash2 size={14} />
                                </button>
                            )}

                            {/* File Input */}
                            <div className="mb-3">
                                <label className="block text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                                    Arquivo
                                </label>
                                <input
                                    type="file"
                                    onChange={(e) => handleFileChange(entry.id, e.target.files ? e.target.files[0] : null)}
                                    className="block w-full text-sm text-slate-500 dark:text-slate-400
                                      file:mr-4 file:py-2 file:px-4
                                      file:rounded-full file:border-0
                                      file:text-xs file:font-semibold
                                      file:bg-primary/10 file:text-primary
                                      hover:file:bg-primary/20
                                      cursor-pointer
                                    "
                                />
                            </div>

                            {/* Name Input */}
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                                    Nome do Arquivo
                                </label>
                                <input
                                    type="text"
                                    value={entry.customName}
                                    onChange={(e) => handleNameChange(entry.id, e.target.value)}
                                    placeholder="Ex: Conta de Luz Jan/2024"
                                    className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-600 rounded-lg text-sm text-gray-900 dark:text-slate-100 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-400 dark:placeholder:text-slate-500"
                                />
                            </div>
                        </div>
                    ))}

                    {/* Add More Button */}
                    <button
                        onClick={handleAddEntry}
                        className="w-full py-3 bg-primary/10 text-primary rounded-xl font-bold text-sm hover:bg-primary/20 transition-all flex items-center justify-center gap-2 border border-primary/10 border-dashed"
                    >
                        <Plus size={16} />
                        Adicionar novo documento
                    </button>

                    {/* Helper Text */}
                    <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded-lg text-xs">
                        <AlertCircle size={14} className="mt-0.5 shrink-0" />
                        <p>
                            Os arquivos serão enviados para o webhook configurado e processados. Certifique-se de nomeá-los corretamente para fácil identificação.
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-100 dark:border-slate-700/50 bg-gray-50 dark:bg-slate-800/80 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-600 dark:text-slate-300 font-semibold text-sm hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                        disabled={loading}
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="px-6 py-2 bg-primary text-white rounded-lg font-bold text-sm hover:bg-primary/90 transition-all shadow-sm shadow-primary/20 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <>
                                <Loader2 size={16} className="animate-spin" />
                                Enviando...
                            </>
                        ) : (
                            <>
                                <Upload size={16} />
                                Enviar documento(s)
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default DocumentUploadModal;
